import React, { useState } from "react";
import { useDeal } from "../lib/store";
import { platformsApi } from "../lib/api";
import * as m from "../lib/model";
import { Card, MoneyInput } from "./shared";
import { Building2, Plus, Trash2, Pencil, Check, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", industry: "", ebitda: 0, revenue: 0, multiplePaid: 0, debt: 0, debtRate: 10, debtYears: 10, ownershipPct: 100, acquiredYear: new Date().getFullYear(), status: "Active", notes: "" };
export const PLATFORM_STATUSES = ["Active", "Under LOI", "Closed", "Under Operation", "Integrating", "Exited"];
const statusTone = (s) => (["Active", "Under Operation"].includes(s) ? "go" : s === "Exited" ? "" : "amber");

function PlatformForm({ value, onChange, onSave, onCancel, saving }) {
  const [thinking, setThinking] = useState(false);
  const f = (k, type = "text") => ({ className: `inp ${type === "number" ? "mono" : ""}`, type, value: value[k] ?? "", onChange: (e) => onChange({ ...value, [k]: type === "number" ? +e.target.value || 0 : e.target.value }), "data-testid": `pf-${k}` });
  const money = (k) => ({ value: value[k] ?? "", onChange: (v) => onChange({ ...value, [k]: +v || 0 }), testid: `pf-${k}` });
  const genThesis = async () => {
    if (!String(value.industry || "").trim()) return toast.error("Add the industry first — the thesis is written off it");
    setThinking(true);
    try {
      const r = await platformsApi.thesis({ name: value.name, industry: value.industry, ebitda: +value.ebitda || 0, revenue: +value.revenue || 0, multiplePaid: +value.multiplePaid || 0, ownershipPct: +value.ownershipPct || 100, acquiredYear: +value.acquiredYear || null });
      onChange({ ...value, notes: r.notes });
      toast.success("Thesis drafted — edit it however you like");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft the thesis"); }
    finally { setThinking(false); }
  };
  return (
    <div className="pf" data-testid="platform-form">
      <div className="grid g3">
        <div className="field"><label>Platform name</label><input {...f("name")} placeholder="e.g. Lone Star HVAC Holdings" /></div>
        <div className="field"><label>Industry</label><input {...f("industry")} /></div>
        <div className="field"><label>Acquired (year)</label><input {...f("acquiredYear", "number")} /></div>
        <div className="field"><label>Status</label>
          <select className="inp" value={value.status || "Active"} onChange={(e) => onChange({ ...value, status: e.target.value })} data-testid="pf-status">
            {PLATFORM_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="field"><label>EBITDA ($)</label><MoneyInput {...money("ebitda")} /></div>
        <div className="field"><label>Revenue ($)</label><MoneyInput {...money("revenue")} /></div>
        <div className="field"><label>Multiple paid (× EBITDA)</label><input {...f("multiplePaid", "number")} step="0.1" /></div>
        <div className="field"><label>Debt outstanding ($)</label><MoneyInput {...money("debt")} /></div>
        <div className="field"><label>Debt rate %</label><input {...f("debtRate", "number")} step="0.1" /></div>
        <div className="field"><label>Debt term (yrs)</label><input {...f("debtYears", "number")} /></div>
        <div className="field"><label>Our ownership %</label><input {...f("ownershipPct", "number")} /></div>
        <div className="field" style={{ gridColumn: "span 3" }}>
          <label>Notes
            <button type="button" className="btn btn-sm pf-ai" onClick={genThesis} disabled={thinking} data-testid="pf-ai-thesis">
              <Sparkles size={11} />{thinking ? "Writing…" : "AI thesis from industry"}
            </button>
          </label>
          <textarea className="inp" rows={4} value={value.notes ?? ""} onChange={(e) => onChange({ ...value, notes: e.target.value })} placeholder="Thesis, add-on criteria, integration playbook…" data-testid="pf-notes" />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button className="btn btn-sm" onClick={onCancel} data-testid="pf-cancel"><X size={12} style={{ marginRight: 4 }} />Cancel</button>
        <button className="btn btn-sm btn-primary" onClick={onSave} disabled={saving || !value.name.trim()} data-testid="pf-save"><Check size={12} style={{ marginRight: 4 }} />{saving ? "Saving…" : "Save platform"}</button>
      </div>
    </div>
  );
}

export function PlatformsCard() {
  const { platforms, refreshPlatforms } = useDeal();
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await platformsApi.create(draft); else await platformsApi.update(editing, draft);
      await refreshPlatforms();
      setEditing(null);
      toast.success("Platform saved");
    } catch { toast.error("Could not save platform"); } finally { setSaving(false); }
  };
  const remove = async (p) => { await platformsApi.remove(p.id); await refreshPlatforms(); toast.success(`${p.name} removed`); };

  return (
    <Card title="Portfolio Platforms" testid="platforms-card">
      <div className="ra-sub" style={{ marginBottom: 10 }}>Companies you already own. New deals can be underwritten as add-ons to a platform (consolidated leverage, DSCR, multiple arbitrage, accretion) or registered as a new platform for the roll-up.</div>
      {platforms.length === 0 && editing !== "new" && <div className="bullet">No platforms yet — add the first company you own.</div>}
      {platforms.map((p) => editing === p.id ? <PlatformForm key={p.id} value={draft} onChange={setDraft} onSave={save} onCancel={() => setEditing(null)} saving={saving} /> : (
        <div className="plat-row" key={p.id} data-testid={`platform-${p.id}`}>
          <Building2 size={16} />
          <div className="plat-main">
            <b>{p.name}</b><span className="ra-sub"> · {p.industry || "—"}{p.acquiredYear ? ` · acquired ${p.acquiredYear}` : ""}</span> <span className={`pill ${statusTone(p.status || "Active")}`} data-testid={`platform-status-${p.id}`}>{p.status || "Active"}</span>
            <div className="plat-stats mono">EBITDA {m.fmt$(p.ebitda)} · {m.fmtX(p.multiplePaid)} paid · debt {m.fmt$(p.debt)} · {p.ownershipPct}% owned</div>
          </div>
          <button className="icon-btn" title="Edit" onClick={() => { setEditing(p.id); setDraft({ ...EMPTY, ...p }); }} data-testid={`platform-edit-${p.id}`}><Pencil size={12} /></button>
          <button className="icon-btn" title="Remove" onClick={() => remove(p)} data-testid={`platform-del-${p.id}`}><Trash2 size={12} /></button>
        </div>
      ))}
      {editing === "new" ? <PlatformForm value={draft} onChange={setDraft} onSave={save} onCancel={() => setEditing(null)} saving={saving} />
        : <button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => { setEditing("new"); setDraft(EMPTY); }} data-testid="platform-add"><Plus size={12} style={{ marginRight: 4 }} />Add platform</button>}
    </Card>
  );
}
